import { FC, ReactNode, useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useCurrentUser, useSessionWatcher } from '@centrix/api-client';
import { LoadingScreen, SessionWarningDialog } from '@centrix/shared-ui';

interface ProtectedRouteProps {
  children: ReactNode;
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user, isLoading, error } = useCurrentUser();

  const isAuthenticated = !isLoading && !!user && !error;

  const redirectToLogin = useCallback(() => {
    const next = encodeURIComponent(location.pathname + location.search);
    navigate(`/login?next=${next}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const { showWarning, msUntilLogout, extendSession } = useSessionWatcher({
    enabled: isAuthenticated,
    onExpired: redirectToLogin,
  });

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error || !user) {
    const redirectUrl = `/login?next=${encodeURIComponent(location.pathname + location.search)}`;
    return <Navigate to={redirectUrl} replace />;
  }

  return (
    <>
      {children}
      <SessionWarningDialog
        isOpen={showWarning}
        msUntilLogout={msUntilLogout}
        onStaySignedIn={extendSession}
        onSignOut={redirectToLogin}
      />
    </>
  );
};
